import React, { useMemo, useCallback } from "react"
import { Collapse, Empty, Switch, Tag, Tooltip, Popover } from "antd"
import { CaretRightOutlined, SettingOutlined, WarningOutlined } from "@ant-design/icons"
import { ExportMapGroup, ExportMapVariable } from "src/types/exportMap.types"
import { useExportMapStore } from "stores/exportMapStore"
import ExportMapVariableCard from "./ExportMapVariableCard.component"

interface ExportMapTreeProps {
  groups: ExportMapGroup[]
  rootVariables: ExportMapVariable[]
  searchTerm: string
}

/**
 * Hierarchical tree view for export map variables.
 * Groups variables by their path hierarchy and supports expand/collapse.
 * Root-level variables (without //) are displayed directly without nesting.
 */
const ExportMapTree: React.FC<ExportMapTreeProps> = ({ groups, rootVariables, searchTerm }) => {
  const { toggleGroupExpanded, toggleMaxMulti, isMaxMultiGroup, getMaxMultiValidationErrors } = useExportMapStore()

  /**
   * Filter groups based on search term
   */
  const filterGroup = useCallback(
    (group: ExportMapGroup): ExportMapGroup | null => {
      const term = searchTerm.toLowerCase()

      // Filter variables in this group
      const filteredVariables = group.variables.filter(
        (v) =>
          v.fullLabel.toLowerCase().includes(term) ||
          v.variableCode.toLowerCase().includes(term)
      )

      // Recursively filter subgroups
      const filteredSubgroups = group.subgroups
        .map((sg) => filterGroup(sg))
        .filter((sg): sg is ExportMapGroup => sg !== null)

      // Include this group if it has matching variables or subgroups
      if (filteredVariables.length > 0 || filteredSubgroups.length > 0) {
        return {
          ...group,
          variables: filteredVariables,
          subgroups: filteredSubgroups,
          isExpanded: searchTerm ? true : group.isExpanded, // Auto-expand when searching
        }
      }

      return null
    },
    [searchTerm]
  )

  const filteredGroups = useMemo(() => {
    // Ensure groups is always an array
    const safeGroups = Array.isArray(groups) ? groups : []
    if (!searchTerm) return safeGroups
    return safeGroups
      .map((g) => filterGroup(g))
      .filter((g): g is ExportMapGroup => g !== null)
  }, [groups, searchTerm, filterGroup])

  // Filter root variables based on search term
  const filteredRootVariables = useMemo(() => {
    // Ensure rootVariables is always an array
    const safeRootVariables = Array.isArray(rootVariables) ? rootVariables : []
    if (!searchTerm) return safeRootVariables
    const term = searchTerm.toLowerCase()
    return safeRootVariables.filter(
      (v) =>
        v.fullLabel.toLowerCase().includes(term) ||
        v.variableCode.toLowerCase().includes(term)
    )
  }, [rootVariables, searchTerm])

  /**
   * Render a single group with its variables and subgroups
   */
  const renderGroup = useCallback(
    (group: ExportMapGroup, level: number = 0) => {
      const hasContent =
        group.variables.length > 0 || group.subgroups.length > 0
      if (!hasContent) return null

      const variableCount =
        group.variables.length +
        group.subgroups.reduce(
          (acc, sg) => acc + sg.variables.length,
          0
        )

      const hasBoolChildren = group.variables.some((v) => v.type === "BOOL")
      const isMaxMulti = isMaxMultiGroup(group.path)
      const validationErrors = getMaxMultiValidationErrors()
      const groupKey = group.path.join("//")
      const groupErrors = validationErrors.get(groupKey)

      return (
        <Collapse
          key={group.path.join("//")}
          bordered={false}
          expandIcon={({ isActive }) => (
            <CaretRightOutlined rotate={isActive ? 90 : 0} />
          )}
          activeKey={group.isExpanded ? [group.label] : []}
          onChange={() => toggleGroupExpanded(group.path)}
          className="bg-transparent"
          items={[
            {
              key: group.label,
              label: (
                <div className="flex items-center justify-between w-full">
                  <span className="font-medium">{group.label}</span>
                  <div className="flex items-center gap-2">
                    {hasBoolChildren && (
                      <div
                        className="flex items-center gap-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {groupErrors && (
                          <Tooltip title={groupErrors.join("; ")}>
                            <WarningOutlined className="text-red-500 text-xs" />
                          </Tooltip>
                        )}
                        {isMaxMulti && (
                          <Tag color="#36c3ed" style={{ fontSize: 11, lineHeight: '18px', padding: '0 5px', margin: 0 }}>
                            Max-Multi
                          </Tag>
                        )}
                        <Popover
                          trigger="click"
                          placement="bottomRight"
                          content={
                            <div style={{ maxWidth: 280 }}>
                              <div className="flex items-center gap-2 py-1">
                                <Switch
                                  size="small"
                                  checked={isMaxMulti}
                                  onChange={() => toggleMaxMulti(group.path)}
                                  style={{
                                    backgroundColor: isMaxMulti ? "#36c3ed" : undefined,
                                  }}
                                />
                                <span className="text-sm">Max-Multi export</span>
                              </div>
                              <p className="text-xs text-gray-400 mt-1 mb-0">
                                Exports each BOOL as its assigned code instead of 0/1.
                                This is a one-way export format — data exported with
                                max-multi codes cannot be re-imported as BOOL.
                              </p>
                            </div>
                          }
                        >
                          <SettingOutlined
                            className="text-gray-400 hover:text-gray-600 cursor-pointer"
                            style={{ fontSize: 13 }}
                          />
                        </Popover>
                      </div>
                    )}
                    <span className="text-gray-400 text-sm">
                      {variableCount} variable{variableCount !== 1 ? "s" : ""}
                    </span>
                  </div>
                </div>
              ),
              children: (
                <div className="pl-2">
                  {/* Variables in this group */}
                  {group.variables.map((variable) => (
                    <ExportMapVariableCard
                      key={variable.fullLabel}
                      variable={variable}
                    />
                  ))}

                  {/* Nested subgroups */}
                  {group.subgroups.map((subgroup) =>
                    renderGroup(subgroup, level + 1)
                  )}
                </div>
              ),
            },
          ]}
        />
      )
    },
    [toggleGroupExpanded, toggleMaxMulti, isMaxMultiGroup, getMaxMultiValidationErrors]
  )

  if (filteredGroups.length === 0 && filteredRootVariables.length === 0) {
    return (
      <div className="flex items-center justify-center py-8">
        <Empty
          description={
            searchTerm
              ? "No variables match your search"
              : "No variables available"
          }
        />
      </div>
    )
  }

  return (
    <div className="p-2">
      {/* Render root-level variables directly (not wrapped in groups) */}
      {filteredRootVariables.map((variable) => (
        <ExportMapVariableCard key={variable.fullLabel} variable={variable} />
      ))}

      {/* Render grouped variables */}
      {filteredGroups.map((group) => renderGroup(group))}
    </div>
  )
}

export default ExportMapTree
